import { useAskAIContext } from '@/context/AskAiContext';
import { getUniqueFileName } from '@/features/msGraph/helper';
import { truncateFilename } from '@/utils/constants';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Box, Chip, FormHelperText, Grid, Stack, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import React, { useRef, useState } from 'react';

const getFileDisplayName = (file) => file?.name || file?.fileName || file?.originalName || 'Attachment';

const DisplayFile = ({ file, onRemove }) => {
	const fullName = getFileDisplayName(file);
	const name = truncateFilename(fullName, 40);
	return (
		<Tooltip size='small' title={name !== fullName ? fullName : ''}>
			<Chip
				icon={<AttachFileIcon />}
				label={name}
				variant='outlined'
				onDelete={onRemove}
			/>
		</Tooltip>
	);
};

const AskAIFiles = () => {
	const { removeSelectedSource, selectedSources, session } = useAskAIContext();

	if (!session || selectedSources.length === 0) return null;

	return (
		<Grid container spacing={0.5}>
			{selectedSources.map((file, index) => (
				<DisplayFile
					key={file.id ?? `${getFileDisplayName(file)}-${index}`}
					file={file}
					onRemove={() => removeSelectedSource(file)}
				/>
			))}
		</Grid>
	);
};

const appendLocalFiles = (currentFiles, incomingFiles) => {
	const processedFiles = incomingFiles.map((file) => {
		const uniqueName = getUniqueFileName(currentFiles || [], file.name);
		if (uniqueName !== file.name) {
			return new File([file], uniqueName, { type: file.type });
		}
		return file;
	});

	return [...(currentFiles || []), ...processedFiles];
};

const AskAiTextfield = () => {
	const {
		contextType,
		draft,
		handleSend,
		isBusy,
		session,
		setDraft,
		setSelectedSources,
	} = useAskAIContext();
	const [isDragging, setIsDragging] = useState(false);
	const dragDepthRef = useRef(0);
	const fileInputRef = useRef(null);
	const theme = useTheme();

	const sessionId = session?.sessionId ?? session?.id;
	const disabled = !sessionId || isBusy || Boolean(session?.disabledReason);

	const placeholder = `Ask about this client,${contextType !== 'client' ? ' deal,' : ''} policy position, notes, or correspondence...`;

	const addLocalFiles = (fileList) => {
		const files = Array.from(fileList ?? []).filter((file) => file instanceof File);
		if (files.length === 0) return;
		setSelectedSources((current) => appendLocalFiles(current, files));
	};

	const handleDragEnter = (event) => {
		if (disabled || !event.dataTransfer?.types?.includes('Files')) return;
		event.preventDefault();
		event.stopPropagation();
		dragDepthRef.current += 1;
		setIsDragging(true);
	};

	const handleDragLeave = (event) => {
		if (disabled) return;
		event.preventDefault();
		event.stopPropagation();
		dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
		if (dragDepthRef.current === 0) {
			setIsDragging(false);
		}
	};

	const handleDragOver = (event) => {
		if (disabled || !event.dataTransfer?.types?.includes('Files')) return;
		event.preventDefault();
		event.stopPropagation();
		event.dataTransfer.dropEffect = 'copy';
	};

	const handleDrop = (event) => {
		if (disabled) return;
		event.preventDefault();
		event.stopPropagation();
		dragDepthRef.current = 0;
		setIsDragging(false);
		addLocalFiles(event.dataTransfer?.files);
	};

	return (
		<Box
			onDragEnter={handleDragEnter}
			onDragLeave={handleDragLeave}
			onDragOver={handleDragOver}
			onDrop={handleDrop}
			sx={{
				position: 'relative',
				borderRadius: '8px',
				border: isDragging
					? `2px dashed ${theme.palette.primary.main}`
					: `1px solid ${theme.palette.divider}`,
				backgroundColor: isDragging ? theme.palette.action.selected : theme.palette.background.paper,
				p: 1.5,
				transition: 'border-color 0.15s ease, background-color 0.15s ease',
				...(disabled && {
					backgroundColor: theme.palette.action.disabledBackground,
				}),
			}}
		>
			<input
				ref={fileInputRef}
				type='file'
				multiple
				hidden
				onChange={(event) => {
					addLocalFiles(event.target.files);
					event.target.value = '';
				}}
			/>
			<Stack spacing={0.5}>
				<AskAIFiles />
				<TextField
					placeholder={placeholder}
					value={draft}
					onChange={(event) => setDraft(event.target.value)}
					onKeyDown={(event) => {
						if (event.key === 'Enter' && !event.shiftKey) {
							event.preventDefault();
							handleSend();
						}
					}}
					multiline
					minRows={3}
					disabled={disabled}
					fullWidth
					variant='standard'
					sx={{
						'& .MuiInputBase-root': {
							backgroundColor: 'transparent !important',
						},
						'& .MuiInputBase-input': {
							backgroundColor: 'transparent !important',
						},
					}}
					slotProps={{
						input: {
							disableUnderline: true,
							sx: {
								alignItems: 'flex-start',
								color: theme.palette.text.primary,
							},
						},
					}}
				/>
				<FormHelperText sx={{ m: 0, color: theme.palette.text.secondary }}>
					<Box
						component='span'
						role='button'
						tabIndex={disabled ? -1 : 0}
						aria-disabled={disabled}
						onClick={() => {
							if (disabled) return;
							fileInputRef.current?.click();
						}}
						onKeyDown={(event) => {
							if (disabled) return;
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								fileInputRef.current?.click();
							}
						}}
						sx={{
							color: theme.palette.primary.main,
							textDecoration: 'underline',
							cursor: disabled ? 'default' : 'pointer',
							opacity: disabled ? 0.5 : 1,
						}}
					>
						Upload
					</Box>
					{' or drop a file right here'}
				</FormHelperText>
			</Stack>
			{isDragging && (
				<Box
					sx={{
						position: 'absolute',
						inset: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						pointerEvents: 'none',
						borderRadius: '8px',
					}}
				>
					<Typography variant='card_header' sx={{ color: theme.palette.primary.main }}>
						Drop file here
					</Typography>
				</Box>
			)}
		</Box>
	);
};

export default AskAiTextfield;
